"use client";

import { FormEvent, useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { isAdmin } from "@/lib/admin";

const DEV_MODE_KEY = "hoav:devMode";

// Mounts the dev tooling: a console-triggered admin login, the Dev button it
// reveals, and a global error trap. Rendered once inside the AuthProvider.
//
//   window.devLogin()       → open the dev login (or unlock instantly if the
//                             admin account is already signed in)
//   window.heroLastError()  → print the most recent captured error
//
// The Dev button + /dev panel only appear for the admin account. The truly
// destructive actions (clearing the scoreboard) are additionally enforced
// server-side by RLS, so this client gate is just for hiding the UI.
export default function DevConsole() {
  const { profile } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  // Restore the dev-mode flag for this browser session (post-mount, so the
  // server-rendered output — nothing — matches the first client paint).
  useEffect(() => {
    if (window.sessionStorage.getItem(DEV_MODE_KEY) === "1") setDevMode(true);
  }, []);

  const enableDevMode = useCallback(() => {
    window.sessionStorage.setItem(DEV_MODE_KEY, "1");
    setDevMode(true);
  }, []);

  // Expose window.devLogin. Re-bound when `profile` changes so the "already
  // signed in as admin" shortcut sees the latest auth state.
  useEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    const open = () => {
      if (isAdmin(profile)) {
        enableDevMode();
        console.log("[HeroOrAVillain] dev mode on — Dev button is now visible.");
        return;
      }
      setStatus("idle");
      setErrorMsg("");
      setLoginOpen(true);
    };
    // Bound under a few spellings so it's hard to mistype from memory.
    w.devLogin = open;
    w.devlogin = open;
    w.devLogin_ = open;
    return () => {
      delete w.devLogin;
      delete w.devlogin;
      delete w.devLogin_;
    };
  }, [profile, enableDevMode]);

  const handleDevLogin = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error || !data.user) {
      setStatus("error");
      setErrorMsg(error?.message ?? "Sign-in failed.");
      return;
    }
    // Verify against the server-side admin flag — not anything in the client.
    const { data: prof } = await supabase
      .from("profiles")
      .select("is_admin")
      .eq("id", data.user.id)
      .single();
    if (!prof?.is_admin) {
      // Valid credentials, but not a dev account — don't leave them signed in
      // via this form.
      await supabase.auth.signOut();
      setStatus("error");
      setErrorMsg("Not a dev account.");
      return;
    }
    enableDevMode();
    setLoginOpen(false);
    setStatus("idle");
    setPassword("");
  };

  // Global error capture: persist + expose the last error so a fast-dismissed
  // crash can still be read back.
  useEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    const save = (info: Record<string, unknown>) => {
      const record = { ...info, url: window.location.href, at: new Date().toISOString() };
      try {
        window.localStorage.setItem(
          "heroOrAVillain:lastError",
          JSON.stringify(record),
        );
      } catch {
        /* storage may be unavailable */
      }
      console.error("[HeroOrAVillain] captured error:", record);
    };

    const onError = (e: ErrorEvent) =>
      save({ message: e.message, stack: e.error?.stack, source: `${e.filename}:${e.lineno}:${e.colno}` });
    const onRejection = (e: PromiseRejectionEvent) =>
      save({
        message: `Unhandled rejection: ${e.reason?.message ?? e.reason}`,
        stack: e.reason?.stack,
      });

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    w.heroLastError = () => {
      try {
        const raw = window.localStorage.getItem("heroOrAVillain:lastError");
        if (!raw) {
          console.log("[HeroOrAVillain] no error captured yet.");
          return null;
        }
        const parsed = JSON.parse(raw);
        console.log("[HeroOrAVillain] last error:", parsed);
        return parsed;
      } catch {
        return null;
      }
    };

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      delete w.heroLastError;
    };
  }, []);

  const showDevButton = devMode && isAdmin(profile);

  return (
    <>
      {loginOpen && (
        <div className="fixed left-1/2 top-4 z-[100] w-[min(92vw,22rem)] -translate-x-1/2 rounded-2xl border border-accent/50 bg-background/95 p-4 shadow-[0_0_40px_rgba(0,0,0,0.6)] backdrop-blur">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-accent">
              Dev Login
            </h3>
            <button
              onClick={() => setLoginOpen(false)}
              aria-label="Close"
              className="flex h-6 w-6 items-center justify-center rounded-full border border-white/20 text-white/60 hover:border-accent hover:text-accent"
            >
              ✕
            </button>
          </div>
          <form onSubmit={handleDevLogin} className="flex flex-col gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              autoComplete="email"
              className="rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-accent"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              className="rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-accent"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-1 rounded-lg bg-accent px-4 py-2 text-sm font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:opacity-50"
            >
              {status === "loading" ? "..." : "Unlock Dev Tools"}
            </button>
            {status === "error" && (
              <div className="text-center text-xs text-red-400">{errorMsg}</div>
            )}
          </form>
        </div>
      )}

      {showDevButton && (
        <a
          href="/dev"
          target="_blank"
          rel="noreferrer"
          className="fixed bottom-20 right-4 z-[90] rounded-full border border-accent/60 bg-background/90 px-4 py-2 text-xs font-bold uppercase tracking-widest text-accent shadow-[0_0_20px_rgba(124,58,237,0.4)] backdrop-blur transition hover:bg-accent hover:text-white"
        >
          🛠 Dev
        </a>
      )}
    </>
  );
}
